// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import Icon from "@insite/mobius/Icon";
import LazyImage from "@insite/mobius/LazyImage";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CartPlusLine from "@store/Library/Components/Toolkit/Icons/CartPlus/CartPlusLine";
import HeartLine from "@store/Library/Components/Toolkit/Icons/Heart/HeartLine";
import { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { StyledIcon } from "@store/MobiusOverrides/Link/Link";
import useProductContext from "@store/Framework/Store/Data/Products/ProductsHooks/useProductContext";
import CardBodyComponent from "@store/Library/Widgets/Card/CardBody";
import { ButtonIcon } from "@store/MobiusOverrides/Button/Props";
import ProductAddToCartButton from "@store/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@store/Overrides/Components/ProductAddToListLink";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React, { useMemo } from "react";
import { css } from "styled-components";

export interface BunzlAutoCompleteProductBodyStyles {
    cardBody?: InjectableCss;
    lazyImage?: InjectableCss;
    rightIconWrapper?: InjectableCss;
    leftIconWrapper?: InjectableCss;
    productAddToCartButton?: ButtonPresentationProps;
    productAddToListLink?: ProductAddToListLinkStyles;
    disabledIcon?: InjectableCss;
}

interface BunzlAutoCompleteProductBodyProps {
    isAuthenticated: boolean;
    autocompleteProductItem: ProductAutocompleteItemModel;
    goToUrl: (url: string) => void;
    onProductAction?: (url: string) => void;
    extendedStyles?: BunzlAutoCompleteProductBodyStyles;
}

const BunzlAutoCompleteProductBody: React.FC<BunzlAutoCompleteProductBodyProps> = ({
    isAuthenticated,
    autocompleteProductItem,
    goToUrl,
    onProductAction,
    extendedStyles,
}) => {
    const productContext = useProductContext();
    const { product, productInfo } = productContext;

    const bunzlAutoCompleteProductBodyStyles: BunzlAutoCompleteProductBodyStyles = useMemo(
        () => ({
            cardBody: {
                css: css`
                    ${isAuthenticated && `padding-top: ${pxToRem(24)}`};
                    position: relative;
                    min-height: ${isAuthenticated ? pxToRem(125) : pxToRem(175)};
                    height: 50%;
                `,
            },
            lazyImage: {
                css: css`
                    width: 100%;
                    height: ${isAuthenticated ? pxToRem(125) : pxToRem(150)};
                    object-fit: contain;
                    cursor: pointer;
                    > img {
                        height: 100%;
                        width: 100%;
                        object-fit: contain;
                    }
                `,
            },
            rightIconWrapper: {
                css: css`
                    position: absolute;
                    top: 0;
                    right: 0;
                `,
            },
            leftIconWrapper: {
                css: css`
                    position: absolute;
                    top: 0;
                    left: 0;
                `,
            },
            productAddToCartButton: {
                css: css`
                    border: none;
                    padding: 0;
                    transition: 0.5s ease-in-out;
                    :disabled {
                        background-color: inherit;
                        > ${ButtonIcon} {
                            color: ${getColor("common.disabled")};

                            :hover {
                                color: ${getColor("common.disabled")};

                                > svg {
                                    color: ${getColor("common.disabled")};
                                }
                            }

                            > svg {
                                color: ${getColor("common.disabled")};
                            }
                        }
                    }

                    :not(:disabled) {
                        :hover {
                            background-color: inherit;
                        }

                        > ${ButtonIcon} {
                            color: ${getColor("primary.main")};
                            :hover {
                                color: ${getColor("secondary.main")};
                            }

                            > svg {
                                color: ${getColor("primary.main")};
                                :hover {
                                    color: ${getColor("secondary.main")};
                                }
                            }
                        }
                    }

                    &&& > ${ButtonIcon} {
                        margin: 0;
                        height: ${pxToRem(32)};
                        width: ${pxToRem(32)};
                        transition: 0.5s ease-in-out;
                        > svg {
                            height: ${pxToRem(32)};
                            width: ${pxToRem(32)};
                        }
                    }
                `,
                buttonType: "outline",
                icon: {
                    src: CartPlusLine,
                    position: "left",
                },
            },
            productAddToListLink: {
                link: {
                    css: css`
                        :hover {
                            ${StyledIcon} {
                                color: ${getColor("secondary.main")};
                            }
                        }
                    `,
                    icon: {
                        iconProps: {
                            size: 28,
                            color: "primary",
                            css: css`
                                margin-right: 0;
                                background-color: ${getColor("primary.contrast")};
                                border-radius: 50%;
                                padding: 0;
                                margin-top: ${pxToRem(4)};
                            `,
                        },
                    },
                },
            },
            disabledIcon: {
                css: css`
                    cursor: not-allowed;
                    margin-top: ${pxToRem(4)};
                `,
            },
        }),
        [isAuthenticated],
    );

    const styles = useMergeProps(bunzlAutoCompleteProductBodyStyles, extendedStyles);

    return (
        <CardBodyComponent extendedStyles={styles.cardBody} data-test-selector="autoComplete_productBody">
            <LazyImage
                src={autocompleteProductItem.image}
                altText={autocompleteProductItem.title}
                height="100%"
                width="100%"
                mergeCss
                onClick={() => goToUrl(autocompleteProductItem.url)}
                css={styles.lazyImage?.css}
                data-test-selector="autoComplete_productBody_image"
            />
            {isAuthenticated && product && productInfo && (
                <>
                    <StyledWrapper
                        title={translate("Add to Cart")}
                        css={styles.rightIconWrapper?.css}
                        data-test-selector="autoComplete_productBody_addToCartWrapper"
                    >
                        {!product.isVariantParent ? (
                            <ProductAddToCartButton
                                labelOverride=""
                                onAddToCartClick={() => onProductAction?.(autocompleteProductItem.url)}
                                extendedStyles={styles.productAddToCartButton}
                                data-test-selector="autoComplete_productBody_addToCart"
                            />
                        ) : (
                            <Icon
                                size={32}
                                color="common.disabled"
                                {...styles.disabledIcon}
                                src={CartPlusLine}
                                data-test-selector="autoComplete_productBody_addToCartDisabled"
                            />
                        )}
                    </StyledWrapper>
                    <StyledWrapper
                        css={styles.leftIconWrapper?.css}
                        title={translate("Add to Wish List")}
                        onClick={
                            productContext?.product.canAddToWishlist
                                ? () => onProductAction?.(autocompleteProductItem.url)
                                : undefined
                        }
                        data-test-selector="autoComplete_productBody_addToListWrapper"
                    >
                        {productContext?.product.canAddToWishlist ? (
                            <ProductAddToListLink
                                extendedStyles={styles.productAddToListLink}
                                showAsLink
                                hideIcon={false}
                                labelOverride=""
                                data-test-selector="autoComplete_productBody_addToList"
                            />
                        ) : (
                            <Icon
                                size={28}
                                color="common.disabled"
                                css={styles.disabledIcon?.css}
                                src={HeartLine}
                                data-test-selector="autoComplete_productBody_addToListDisabled"
                            />
                        )}
                    </StyledWrapper>
                </>
            )}
        </CardBodyComponent>
    );
};

export default BunzlAutoCompleteProductBody;
